import { QueryClient, QueryCache, QueryFunction } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { buildApiUrl, getAuthToken } from "@/lib/apiBase";

// Read the CSRF cookie issued by the server's csrfProtection
// middleware so we can mirror it back as `X-CSRF-Token` on writes.
// Cookie is intentionally NOT HttpOnly because only the web client
// (cookie-authenticated) needs to read it — mobile/native uses
// bearer tokens and skips CSRF entirely. Returns "" if no cookie
// is present yet (first page-load before the server set it).
function getCsrfToken(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)iraqsooq_csrf=([^;]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : "";
}

// Build a headers object. The Authorization header is added ONLY when a
// token is present (native shell after login). On the web `getAuthToken()`
// returns null → no header is added → request is byte-identical to before.
// Adds X-CSRF-Token on every request: cheap, and the server only
// validates it on cookie-authenticated writes.
//
// Exported so raw `fetch()` call sites (image upload, etc) can mirror
// the same authentication contract without having to know about the
// internals of bearer-token storage or the CSRF cookie format.
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra || {}) };
  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const csrf = getCsrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;
  // On the native shell the absolute-URL requests bypass the fetch shim in
  // main.tsx, so the CSRF cookie isn't readable (iOS WKWebView never stores
  // it cross-site). Mark the request as native here too, so pre-auth writes
  // (email/phone OTP, before any bearer token exists) satisfy the server CSRF
  // gate on iOS — without this, account registration fails on iOS only.
  if (Capacitor.isNativePlatform()) headers["X-Client"] = "native";
  return headers;
}

function captureApiError(err: any, context?: Record<string, unknown>) {
  if (err?.status >= 500 && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      }
      scope.setTag("api_error", true);
      scope.setExtra("status", err.status);
      Sentry.captureException(err);
    });
  }
}

// throwIfResNotOk is called ONLY when !res.ok, so res.body is consumed exactly once:
// on error → here. On success → safeJsonParse reads it. No double-read issue.
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type") || "";
    let err: any;
    if (contentType.includes("application/json")) {
      const errorData = await res.json().catch(() => ({}));
      err = new Error(errorData.error || errorData.message || `${res.status}: ${res.statusText}`);
      if (errorData.ref) err.ref = errorData.ref;
      if (errorData.field) err.field = errorData.field;
    } else {
      err = new Error(`${res.status}: ${res.statusText}`);
    }
    err.status = res.status;
    throw err;
  }
}

async function safeJsonParse(res: Response) {
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("خطأ في الخادم. يرجى المحاولة مرة أخرى.");
  }
  return res.json();
}

// Only retry on server errors (5xx) or network failures — never on 4xx (client errors).
const isRetryable = (error: any): boolean =>
  (error?.status !== undefined && error.status >= 500) ||
  error?.message?.toLowerCase().includes("network") ||
  error?.message?.toLowerCase().includes("fetch");

// Exponential backoff for cold-start retries: 2s, 4s, 8s (total ~14s)
const coldStartDelay = (attempt: number): number =>
  Math.min(2000 * Math.pow(2, attempt - 1), 8000);

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      const res = await fetch(buildApiUrl(url), {
        method,
        headers: authHeaders(data ? { "Content-Type": "application/json" } : {}),
        body: data ? JSON.stringify(data) : undefined,
        credentials: "include",
        signal: controller.signal,
      });
      clearTimeout(timeout);

      await throwIfResNotOk(res);
      return res;
    } catch (error: any) {
      lastError = error;
      if (attempt < 3 && isRetryable(error)) {
        await new Promise(r => setTimeout(r, coldStartDelay(attempt)));
        continue;
      }
      break;
    }
  }

  // Report 5xx to Sentry after all retries are exhausted (mutations path)
  if (lastError && (lastError as any)?.status >= 500) {
    captureApiError(lastError, { method, url });
  }

  throw lastError;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Build URL properly: queryKey[0] is the path, queryKey[1] (optional) is a params object.
    // queryKey.join("/") is WRONG — if queryKey[1] is an object, join produces "[object Object]".
    const url = queryKey[0] as string;
    const params = queryKey[1] as Record<string, unknown> | undefined;
    const fullUrl = params && Object.keys(params).length > 0
      ? `${url}?${new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined && v !== null && v !== "")
              .map(([k, v]) => [k, String(v)])
          )
        ).toString()}`
      : url;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        const res = await fetch(buildApiUrl(fullUrl), {
          headers: authHeaders(),
          credentials: "include",
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (unauthorizedBehavior === "returnNull" && res.status === 401) {
          return null;
        }

        await throwIfResNotOk(res);
        return await safeJsonParse(res);
      } catch (error: any) {
        lastError = error;
        if (attempt < 3 && isRetryable(error)) {
          await new Promise(r => setTimeout(r, coldStartDelay(attempt)));
          continue;
        }
        break;
      }
    }

    throw lastError;
  };

// Smart retry: never retry 4xx (client errors), retry up to N times on 5xx/network.
// 502/503 (cold start) gets extra retries since server may be warming up.
const retryFn = (maxRetries: number) => (failureCount: number, error: any) => {
  const status = error?.status ?? 0;
  if (status >= 400 && status < 500) return false;
  // Give 502/503 extra retries for cold-start recovery
  const limit = (status === 502 || status === 503) ? Math.max(maxRetries, 3) : maxRetries;
  return failureCount < limit;
};

// TanStack Query retry delay: exponential backoff for cold-start errors
const retryDelayFn = (attempt: number, error: any): number => {
  const status = error?.status ?? 0;
  if (status === 502 || status === 503) {
    // 2s, 4s, 8s — server is cold-starting
    return Math.min(2000 * Math.pow(2, attempt), 8000);
  }
  // Standard exponential backoff for other 5xx
  return Math.min(1000 * Math.pow(2, attempt), 10000);
};

export const queryClient = new QueryClient({
  // Global error handler: redirect to /login on expired session; capture 5xx to Sentry.
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      if (error?.status === 401) {
        // Only redirect when the auth/user check fails — NOT for optional queries
        // (favorites, alerts, etc. returning 401 for guests is normal, not a redirect trigger)
        const key = query.queryKey[0];
        const isAuthQuery = key === "/api/auth/user";
        const isAlreadyOnAuthPage = window.location.pathname.startsWith("/login") ||
          window.location.pathname.startsWith("/register") ||
          window.location.pathname.startsWith("/forgot-password");
        if (isAuthQuery && !isAlreadyOnAuthPage) {
          window.location.href = "/login";
        }
      }
      // 5xx errors are server bugs — capture once at QueryCache level (after all retries)
      if (error?.status >= 500 && import.meta.env.VITE_SENTRY_DSN) {
        Sentry.withScope((scope) => {
          scope.setTag("api_error", true);
          scope.setExtra("queryKey", query.queryKey);
          scope.setExtra("status", error.status);
          Sentry.captureException(error);
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: retryFn(2),
      retryDelay: retryDelayFn,
    },
    mutations: {
      retry: retryFn(1),
      retryDelay: retryDelayFn,
    },
  },
});
